import React from 'react'
import styled from 'styled-components';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import dayGridPlugin from '@fullcalendar/daygrid';
import { addDays, format } from 'date-fns';
import SlotLabelContent from '../../../pms-calendar/components/calendar/SlotLabelContent';
import { EventInfo } from '../../../pms-calendar/lib/EventInfo';
import EventContent from './EventContent';
import { ResourcePayload } from '../../../pms-calendar/lib/ResourcesPayload';
import { OccPayload } from '../../../pms-calendar/lib/OccPayload';
import { EventsPayload } from '../../../pms-calendar/lib/EventsPayload';
import CalendarNavHeader from './CalendarNavHeader';
import { useMediaQuery } from '../../hooks/useMediaQuery';

const CalendarContainer = styled.div`
  /* FullCalendar sudah menyediakan scroller horizontal internal (.fc-scroller) —
     dengan slotMinWidth di bawah, lebar hari tidak lagi diperas mengikuti layar
     sempit (mobile/tablet), melainkan scroll ke samping. overflow-x di sini
     jaga-jaga kalau container luar ikut membatasi lebar. */
  overflow-x: auto;
  table {
    margin-top: 0px;
  }
  .fc {
    background-color: #ffffff;
  }
  /* Label room type / room number: biarkan wrap 2 baris daripada kepotong
     ellipsis, supaya nama panjang (mis. "LUMBUNG TINGKAT") tetap terbaca di
     kolom resourceArea yang sempit (mobile). Discope ke .fc-datagrid-body
     saja — FullCalendar memakai class .fc-datagrid-cell-main yang SAMA
     untuk isi header resourceArea (judul "Rooms Maintenance" + tombol nav
     di CalendarNavHeader), jadi kalau tidak discope, judul header ikut
     ter-word-break di tengah kata ("Maintenanc"/"e") dan terpotong karena
     tinggi baris header sudah fixed (lihat aturan di bawah). */
  .fc-datagrid-body .fc-datagrid-cell-main {
    white-space: normal;
    word-break: break-word;
    line-height: 1.15;
    font-size: 12px;
  }
  /* Header resourceArea (judul "Rooms Maintenance" + tombol ←/TODAY/→): boleh
     wrap ke baris baru tapi HANYA di spasi antar kata (bukan mid-word), dan
     tinggi barisnya dibiarkan menyesuaikan konten (bukan fixed inline height
     dari FullCalendar) supaya baris ke-2 tidak kepotong. */
  .fc-datagrid-header .fc-datagrid-cell-frame {
    height: auto !important;
  }
  .fc-datagrid-header .fc-datagrid-cell-cushion {
    white-space: normal !important;
    overflow: visible !important;
  }
  .fc-datagrid-header .fc-datagrid-cell-main {
    white-space: normal;
    word-break: normal;
    overflow-wrap: normal;
  }
  .fc-event {
    background-color: transparent;
    border-width: 0;
    padding: 0;
  }
  .fc-event-title {
    position: relative;
    z-index: 1;
  }
  .fc-timeline-event-harness {
    top: 0px !important;
  }
  th[colspan='1'].fc-day-today {
    background-color: rgb(222, 244, 255);
    color: white;
  }
  td.fc-day-today {
    background-color: rgb(222, 244, 255);
    color: white;
  }
  th[colspan='1'].fc-day-future,
  th[colspan='1'].fc-day-past {
    background-color: transparent;
  }
  span.fc-datagrid-expander.fc-datagrid-expander-placeholder,
  span.fc-icon,
  span.fc-datagrid-expander {
    display: none;
  }
  div.fc-datagrid-cell-frame {
    justify-content: center !important;
  }
  .fc-timeline-events {
    padding-top: 0px !important;
    padding-bottom: 0px !important;
    height: 36px !important;
  }
  .fc-timeline-event{
    align-items: normal;
  }
  .fc-timeline-lane-frame {
    height: 36px !important;
  }
  .fc-header-toolbar {
    display: none;
  }
  .fc-timeline-slot-cushion:hover {
    text-decoration: none;
  }
  .fc-resource-timeline-divider {
    width: 0px !important;
  }
  .fc-timeline-slot-frame:not(:has(.fc-sticky)) {
    height: 43px !important;
  }
  .fc-timeline-slot-frame:has(.fc-sticky) {
    justify-content: start;
  }
`;

interface CalendarComponentProps {
    startDate: string,
    endDate: string,
    setStartDate: (date: string) => void,
    setEndDate: (date: string) => void,
    occupancy: OccPayload[],
    resourcesPayload: ResourcePayload[],
    eventsPayload: EventsPayload[],
    onCreateFromDrag: (startDate: string, endDate: string, roomTypeId: string, roomId: string) => void,
    onEditRoomMaintenance: (id: string) => void,
    isReadOnly?: boolean,
}


interface ResourceLabelInfo {
    resource: {
        title: string;
    };
}

const renderResourceLabel = (info: ResourceLabelInfo) => <b>{info.resource.title}</b>;

const CalendarComponent: React.FC<CalendarComponentProps> = ({
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    occupancy,
    resourcesPayload,
    eventsPayload,
    onCreateFromDrag,
    onEditRoomMaintenance,
    isReadOnly = false,
}) => {
    // Desktop dapat kolom resourceArea lebih lega (mirip CalendarBooking.tsx di
    // pms-calendar yang pakai "20%") — di layar sempit (mobile/tablet) tetap
    // FIXED 150px seperti sebelumnya supaya nama room type panjang & tombol nav
    // tidak diperas/kepotong (lihat komentar resourceAreaWidth & CalendarNavHeader).
    const isDesktop = useMediaQuery('(min-width: 992px)');

    return (
        <CalendarContainer>
            <FullCalendar
                plugins={[resourceTimelinePlugin, dayGridPlugin, timeGridPlugin, interactionPlugin]}
                headerToolbar={{
                    left: '',
                    center: '',
                    right: '',
                }}
                visibleRange={{
                    start: startDate,
                    // FullCalendar visibleRange.end bersifat exclusive — tambah 1 hari di sini
                    // saja (khusus untuk kalender), supaya endDate tetap merepresentasikan hari
                    // terakhir yang sebenarnya untuk keperluan lain (query data, dst). Sama
                    // seperti fix di CalendarBooking.tsx (pms-calendar).
                    end: format(addDays(new Date(endDate), 1), 'yyyy-MM-dd'),
                }}
                height="auto"
                initialView="resourceTimeline"
                editable={false}
                eventResourceEditable={true}
                resources={resourcesPayload}
                resourceAreaHeaderContent={
                    <CalendarNavHeader
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        isDesktop={isDesktop}
                    />
                }
                resourceLabelDidMount={(info) => {
                    const type = info.resource.extendedProps.type;
                    let backgroundColor = '#ffffff';


                    if (type === 'roomType') {
                        backgroundColor = '#d3d3d3';
                    }

                    info.el.style.backgroundColor = backgroundColor;
                }}
                events={eventsPayload}
                eventDidMount={(info) => {
                    if (info.el) {
                        const eventTd = info.el.closest("td");

                        if (eventTd) {
                            if (info.event.extendedProps.type === 'availability') {
                                eventTd.style.backgroundColor = "#d3d3d3";
                            }
                        }
                    }
                }}
                showNonCurrentDates
                selectable={!isReadOnly}
                select={(info) => {
                    if (isReadOnly) return;
                    // Sama seperti drag-create booking di CalendarBooking.tsx (pms-calendar),
                    // tapi targetnya form "Create Room Maintenance" — halaman ini memang
                    // khusus mengelola blok kamar, bukan reservasi tamu. Guard 'unsigment'
                    // dipertahankan untuk konsistensi meski baris Unassigned tidak dipakai
                    // di kalender ini (showUnsigment selalu false).
                    const status = info.resource?.extendedProps.status;
                    if (status === 'unsigment') return;

                    const isAllDay = info.allDay;
                    const adjustedEndStr = isAllDay
                        ? new Date(new Date(info.endStr).getTime() - 1).toISOString().split("T")[0]
                        : info.endStr;

                    onCreateFromDrag(info.startStr, adjustedEndStr, info.resource?.extendedProps?.room_type_id || '', info.resource?.id || '');
                }}
                slotLabelFormat={[
                    { month: 'long', year: 'numeric' },
                    { day: 'numeric', weekday: 'short' },
                ]}
                slotLabelContent={(info: { text: string, level: number }) => <SlotLabelContent info={info} occupancy={occupancy} />}
                eventContent={(eventInfo: EventInfo) => <EventContent eventInfo={eventInfo} />}
                eventClick={(info) => {
                    // Hanya blok room maintenance yang bisa diklik untuk quick edit — ghost
                    // booking & availability tetap read-only (lihat eventAllow di bawah).
                    if (info.event.extendedProps?.type === 'roomMaintenance') {
                        onEditRoomMaintenance(info.event.id);
                    }
                }}
                eventAllow={(_dropInfo, draggedEvent) => {
                    // 'booking' = ghost booking read-only (lihat EventContent.tsx) — tidak
                    // boleh diseret/diubah dari kalender maintenance ini.
                    if (draggedEvent?.extendedProps.type === 'availability' || draggedEvent?.extendedProps.type === 'booking') {
                        return false;
                    }
                    return true;
                }}
                schedulerLicenseKey="0310067844-fcs-1721029428"
                resourceOrder={'order'}
                resourceLabelContent={renderResourceLabel}
                resourceLabelClassNames={(info) =>
                    info.resource.title === "Unassigned" ? "text-danger" : ""
                }
                // Desktop (>=992px): "20%" (sama seperti CalendarBooking.tsx di
                // pms-calendar) — di layar lebar itu sudah cukup lega untuk nama
                // room type + tombol nav sejajar. Mobile/tablet: FIXED 150px (bukan
                // persentase) — di layar sempit "20%" hanya ~70-80px, cukup untuk
                // memotong nama room type panjang ("LUMBUNG...").
                resourceAreaWidth={isDesktop ? '20%' : '150px'}
                // Kunci lebar minimum tiap kolom hari supaya TIDAK diperas mengikuti
                // lebar layar (itu penyebab tanggal/occupancy/event kepotong di
                // mobile) — kalau total lebar > viewport, FullCalendar scroll ke
                // samping secara native (lihat overflow-x di CalendarContainer).
                slotMinWidth={72}
            />
        </CalendarContainer>
    )
}

export default CalendarComponent